import { z } from 'zod';
/**
 * Audit log operation types
 */
export var AuditOperation;
(function (AuditOperation) {
    AuditOperation["Created"] = "CREATED";
    AuditOperation["Updated"] = "UPDATED";
    AuditOperation["Deleted"] = "DELETED";
    AuditOperation["Login"] = "LOGIN";
    AuditOperation["Logout"] = "LOGOUT";
    /**
     * Soft-delete of a credential, written explicitly by the application (KIR-3746).
     * The `api_keys` audit trigger fires on INSERT/DELETE only, so revoking a
     * management API key — an UPDATE — would otherwise leave no audit trail.
     */
    AuditOperation["Revoked"] = "REVOKED";
    /**
     * A read worth recording, written explicitly by the application (KIR-4445):
     * opening an agent session timeline. Internal only — the `/api/v1` audit
     * feed and SIEM forwarding leave these rows out rather than mislabel a read
     * as a change.
     */
    AuditOperation["Viewed"] = "VIEWED";
})(AuditOperation || (AuditOperation = {}));
/**
 * Audit-metadata keys carrying the management API key behind a write.
 *
 * Written by `v1OrgScopeHook` via `mergeAuditMetadata` (KIR-3747, design D2) and
 * read back by both the public `/api/v1` actor mapper and the internal audit-log
 * UI. They live here, in the shared contract, so those two consumers cannot
 * drift on what counts as an API-key action.
 */
export const AUDIT_METADATA_API_KEY_ID = 'apiKeyId';
export const AUDIT_METADATA_API_KEY_NAME = 'apiKeyName';
/**
 * Org-level key (MDM, plugin) that made a user-less write. Distinct from
 * {@link AUDIT_METADATA_API_KEY_ID} (a management API key), so these rows stay
 * `system` actors.
 */
export const AUDIT_METADATA_ACTOR_KEY_ID = 'actorKeyId';
export const AUDIT_METADATA_ACTOR_KEY_KIND = 'keyKind';
/**
 * `audit_logs.metadata`: two known keys, still open for the rest.
 *
 * The record stays open because the column is genuinely heterogeneous — policy
 * edits write `source`, auth events write `email`/`ip`/`userAgent`, and the
 * explicit API-key revoke row writes `descopeAccessKeyId`/`name`/`keyPrefix`.
 * Only the two keys the UI reads are typed.
 *
 * `.catch(undefined)` on each is deliberate, not defensive noise. This schema is
 * validated through `parseSchema`, so without it a single historical row holding
 * a non-string `apiKeyId` would fail the *entire* list response and blank the
 * audit table. A cosmetic annotation must degrade to "absent", never throw.
 */
export const auditLogMetadataSchema = z
    .object({
    [AUDIT_METADATA_API_KEY_ID]: z.string().optional().catch(undefined),
    [AUDIT_METADATA_API_KEY_NAME]: z.string().optional().catch(undefined),
})
    .catchall(z.unknown());
function readNonEmptyString(metadata, key) {
    const value = metadata?.[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
}
/**
 * Which management API key made this change, if any — the single definition of
 * "was this an API key action?".
 *
 * Presence of `apiKeyId` is the discriminator, never `apiKeyName`: the name is
 * cosmetic, and a key minted without one still has to be identifiable.
 *
 * Both keys are read through a non-empty check because the write side stores
 * `apiKeyName: apiKey.name ?? ''` — an unnamed key arrives as an empty string,
 * not as null. Treating `''` as a name would render a dangling "API:" label.
 */
export function readAuditApiKeyRef(metadata) {
    const id = readNonEmptyString(metadata, AUDIT_METADATA_API_KEY_ID);
    if (id === null) {
        return null;
    }
    return {
        id,
        name: readNonEmptyString(metadata, AUDIT_METADATA_API_KEY_NAME),
    };
}
/**
 * Which org-level key (MDM, plugin) made this user-less change, if any. Only
 * those two kinds are written on the user-less path; anything else is absent.
 */
export function readAuditActorKeyRef(metadata) {
    const id = readNonEmptyString(metadata, AUDIT_METADATA_ACTOR_KEY_ID);
    const kind = metadata?.[AUDIT_METADATA_ACTOR_KEY_KIND];
    if (id === null || (kind !== 'mdm' && kind !== 'plugin')) {
        return null;
    }
    return { id, kind };
}
export const auditLogSchema = z.object({
    id: z.string(),
    tableName: z.string(),
    operation: z.enum([
        AuditOperation.Created,
        AuditOperation.Updated,
        AuditOperation.Deleted,
        AuditOperation.Login,
        AuditOperation.Logout,
        AuditOperation.Revoked,
        AuditOperation.Viewed,
    ]),
    recordId: z.string().nullable(),
    userId: z.string().nullable(),
    organizationId: z.string().nullable(),
    beforeData: z.record(z.string(), z.unknown()).nullable(),
    afterData: z.record(z.string(), z.unknown()).nullable(),
    changedFields: z.record(z.string(), z.unknown()).nullable(),
    timestamp: z.string(),
    metadata: auditLogMetadataSchema.nullable(),
    // Joined user information for display
    userName: z.string().nullable(),
    userEmail: z.string().nullable(),
});
/**
 * Audit logs list response schema
 */
export const auditLogsListSchema = z.object({
    logs: z.array(auditLogSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
});
/**
 * Audit logs pagination query params schema
 * Note: organizationId is now in route params, not query params
 */
export const auditLogPaginationSchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(25),
});
/**
 * Format user display from audit log
 * Priority: name > email > userId > "System"
 */
export function formatUserDisplay(log) {
    if (log.userName)
        return log.userName;
    if (log.userEmail)
        return log.userEmail;
    if (log.userId)
        return log.userId;
    return 'System';
}
//# sourceMappingURL=audit-logs.js.map